// (2026-06-09) MAESTRO de Proveedores — patrón LISTA (página) + FORMULARIO
// (página /create y /edit) + MODAL de detalle, idéntico al maestro de
// Clientes (Clients/ListClients.js + ModalClients.js).
//
// REGLAS:
//  - Conserva TODA la lógica de negocio y llamadas API del módulo de Compras
//    (../../controllers/api_compras): mismos endpoints, mismos campos, misma
//    validación y acciones. Solo cambia la ESTRUCTURA de pantallas y el look.
//  - El formulario crear/editar NO es modal: es una página con ruta
//    /compras/proveedores/create y /compras/proveedores/edit?id=<encriptado>.
//  - Reusa los primitivos de ./ui.js. NADA de @mui.
//  - Conserva AdminLayout y react-toastify.
//
// Esta página (ProveedoresList) mantiene además el acceso a "Categorías de
// gasto" mediante un tab (conserva esa funcionalidad existente con su modal).

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import AdminLayout from "../../layouts/AdminLayout";
import { encryptText } from "../../controllers/api";
import {
  getProveedores, getProveedor, deleteProveedor,
  getCategoriasGasto, createCategoriaGasto, updateCategoriaGasto,
} from "../../controllers/api_compras";
import { monedaCodigo, handleApiError, upper } from "./comprasShared";
import {
  Modal, Field, TextInput, SelectInput, Toggle, BtnPrimary, BtnGhost,
  IconBtn, Card, PageHeader, Badge, Table, Th, Td,
} from "./ui";

/* ================================================================== *
 * TAB PROVEEDORES — lista como página + modal de detalle (sin form).
 * ================================================================== */
function ProveedoresTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null); // proveedor mostrado en el modal de detalle

  const cargar = async () => {
    try { setRows(await getProveedores()); } catch (e) { handleApiError(e); }
  };
  useEffect(() => { cargar(); }, []);

  // Navegación al FORMULARIO (página), igual que el maestro de clientes:
  // crear → /create ; editar → /edit?id=<id encriptado>.
  const goCreate = () => navigate("/compras/proveedores/create");
  const goEdit = (r) => navigate(`/compras/proveedores/edit?id=${encryptText(String(r.id))}`);

  // Ver detalle: precarga el proveedor completo (incluye cuentas) y lo abre
  // en un modal de solo lectura, igual que ModalClients.
  const openDetail = async (r) => {
    try { setDetail(await getProveedor(r.id)); }
    catch (e) { handleApiError(e); }
  };

  const deactivate = async (r) => {
    if (!window.confirm(`¿Desactivar a "${r.nombre}"?`)) return;
    try { await deleteProveedor(r.id); cargar(); } catch (e) { handleApiError(e); }
  };

  return (
    <Card>
      <PageHeader
        title="Proveedores"
        subtitle="Gestione los proveedores de su empresa."
        icon="fa-truck-field"
        actions={
          <button
            type="button"
            onClick={goCreate}
            className="bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2 transition"
          >
            <i className="fas fa-plus" aria-hidden="true" />
            Nuevo proveedor
          </button>
        }
      />

      <Table>
        <thead>
          <tr>
            <Th>Nombre</Th>
            <Th>RIF</Th>
            <Th>Cat. fiscal</Th>
            <Th className="text-right">Días créd.</Th>
            <Th>Moneda</Th>
            <Th>Activo</Th>
            <Th className="text-right">Acciones</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td colSpan={7} className="text-center text-blueGray-400 italic">
                Sin proveedores.
              </Td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-blueGray-50 transition-colors">
              <Td className="font-semibold text-blueGray-700">{r.nombre}</Td>
              <Td>{r.rif}</Td>
              <Td>{r.categoria_fiscal || "—"}</Td>
              <Td className="text-right">{r.dias_credito}</Td>
              <Td>{monedaCodigo(r.moneda_designada_id)}</Td>
              <Td><Badge ok={!!r.activo} labelOk="Si" labelNo="No" /></Td>
              <Td className="text-right">
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.25rem", whiteSpace: "nowrap" }}>
                  <IconBtn icon="fa-up-right-and-down-left-from-center" title="Ver detalle" color="text-emerald-600" onClick={() => openDetail(r)} />
                  <IconBtn icon="fa-pen-to-square" title="Editar" color="text-blue-600" onClick={() => goEdit(r)} />
                  {r.activo && (
                    <IconBtn icon="fa-rectangle-xmark" title="Desactivar" color="text-red-600" onClick={() => deactivate(r)} />
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      <ModalProveedorDetalle
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        message={detail || {}}
      />
    </Card>
  );
}

/* ================================================================== *
 * MODAL DE DETALLE — HERO header + secciones tituladas (estilo
 * ModalClients). Solo presentación; todos los bindings sobre `message`.
 * ================================================================== */
function ModalProveedorDetalle({ isOpen, onClose, message }) {
  if (!isOpen) return null;

  const Initials = (() => {
    const src = (message.nombre || "").trim();
    if (!src) return null;
    const parts = src.split(/\s+/).filter(Boolean);
    const letters = (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
    return letters.toUpperCase() || null;
  })();

  const Item = ({ label, icon, children, full }) => (
    <div className={full ? "w-full" : "w-full sm:w-1/2 lg:w-1/3"}>
      <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4 h-full transition hover:border-sirius-200 hover:shadow-sirius">
        <h3 className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-500 mb-2">
          <i className={`fas ${icon} text-sirius-500 text-[12px]`} aria-hidden="true" />
          {label}
        </h3>
        <div className="text-sm font-medium text-ink-900 break-words leading-relaxed">{children}</div>
      </div>
    </div>
  );

  const SectionTitleHero = ({ icon, children }) => (
    <div className="flex items-center gap-3 mt-9 mb-4">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sirius-50 text-sirius-600 shrink-0">
        <i className={`fas ${icon} text-sm`} aria-hidden="true" />
      </span>
      <h3 className="text-xs uppercase tracking-[0.16em] font-bold text-ink-500 whitespace-nowrap">
        {children}
      </h3>
      <span className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
    </div>
  );

  const cuentas = Array.isArray(message.cuentas) ? message.cuentas : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-sirius-lg w-full max-w-[1100px] max-h-[90vh] flex flex-col overflow-hidden animate-sirius-fade-up">
        {/* HERO HEADER */}
        <div className="relative shrink-0 px-6 md:px-8 py-7 bg-sirius-night overflow-hidden">
          <div className="pointer-events-none absolute inset-0 bg-sirius-mesh opacity-60" />
          <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-spark-500/30 blur-[110px]" />
          <div className="pointer-events-none absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-sirius-500/25 blur-[110px]" />
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="absolute top-4 right-4 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full text-white/80 bg-white/10 hover:bg-white/20 hover:text-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <i className="fas fa-xmark text-lg" aria-hidden="true" />
          </button>

          <div className="relative z-10 flex items-center gap-5 pr-12">
            <div className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-2xl glass-panel flex items-center justify-center text-white font-display font-bold text-2xl md:text-3xl shadow-spark">
              {Initials ? Initials : <i className="fas fa-truck-field text-2xl md:text-3xl" aria-hidden="true" />}
            </div>

            <div className="min-w-0">
              <h2 className="font-display font-bold text-white text-xl md:text-2xl leading-tight break-words">
                {message.nombre || "—"}
              </h2>
              <p className="text-sm text-white/70 mt-0.5 break-words">
                {message.email || "—"}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1.5 bg-white/10 text-white/90 py-1 px-3 rounded-full text-xs font-semibold">
                  <i className="fas fa-id-card" aria-hidden="true" />
                  {message.rif || "—"}
                </span>
                {message.activo == true ? (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-500 text-white py-1 px-3 rounded-full text-xs font-bold">
                    <i className="fas fa-circle-check" aria-hidden="true" />
                    ACTIVO
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 bg-rose-500 text-white py-1 px-3 rounded-full text-xs font-bold">
                    <i className="fas fa-circle-xmark" aria-hidden="true" />
                    INACTIVO
                  </span>
                )}
                {message.categoria_fiscal && (
                  <span className="inline-flex items-center gap-1.5 bg-white/15 text-white py-1 px-3 rounded-full text-xs font-bold">
                    <i className="fas fa-file-invoice" aria-hidden="true" />
                    {message.categoria_fiscal}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 md:px-8 py-6">
          {/* Información general */}
          <SectionTitleHero icon="fa-address-card">Información general</SectionTitleHero>
          <div className="flex flex-wrap gap-3">
            <Item label="Nombre" icon="fa-building">{message.nombre || "—"}</Item>
            <Item label="RIF" icon="fa-id-card">{message.rif || "—"}</Item>
            <Item label="Categoría fiscal" icon="fa-file-invoice">{message.categoria_fiscal || "—"}</Item>
            <Item label="Días de crédito" icon="fa-calendar-day">{message.dias_credito ?? 0}</Item>
            <Item label="Moneda designada" icon="fa-coins">{monedaCodigo(message.moneda_designada_id) || "—"}</Item>
          </div>

          {/* Contacto */}
          <SectionTitleHero icon="fa-address-book">Contacto</SectionTitleHero>
          <div className="flex flex-wrap gap-3">
            <Item label="Teléfono" icon="fa-phone">{message.telefono || "—"}</Item>
            <Item label="Email" icon="fa-envelope">{message.email || "—"}</Item>
            <Item label="Dirección" icon="fa-location-dot" full>{message.direccion || "—"}</Item>
            <Item label="Condición" icon="fa-circle-info">
              {message.activo == true ? (
                <span className="inline-block bg-emerald-500 text-white py-1 px-3 rounded-full text-xs font-bold">ACTIVO</span>
              ) : (
                <span className="inline-block bg-rose-500 text-white py-1 px-3 rounded-full text-xs font-bold">INACTIVO</span>
              )}
            </Item>
          </div>

          {/* Cuentas bancarias (1:M) */}
          <SectionTitleHero icon="fa-building-columns">Cuentas bancarias</SectionTitleHero>
          {cuentas.length === 0 ? (
            <p className="text-sm text-blueGray-400 italic px-1">Sin cuentas bancarias registradas.</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Banco</Th>
                  <Th>Nº de cuenta</Th>
                  <Th>Tipo</Th>
                  <Th>Titular</Th>
                </tr>
              </thead>
              <tbody>
                {cuentas.map((c, i) => (
                  <tr key={c.id ?? i} className="hover:bg-blueGray-50 transition-colors">
                    <Td className="font-semibold text-blueGray-700">{c.banco || "—"}</Td>
                    <Td>{c.numero_cuenta || "—"}</Td>
                    <Td>{c.tipo_cuenta || "—"}</Td>
                    <Td>{c.titular || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {/* FOOTER */}
        <div className="shrink-0 w-full flex justify-center py-4 border-t border-slate-200 bg-white">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-semibold rounded-xl shadow-sirius transition focus:outline-none focus-visible:ring-2 focus-visible:ring-twilight-indigo-400"
          >
            <i className="fas fa-xmark" aria-hidden="true" />
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== *
 * TAB CATEGORÍAS DE GASTO — funcionalidad conservada (modal existente).
 * ================================================================== */
const EMPTY_CAT = { codigo: "", nombre: "", genera_credito_fiscal: true, activo: true };

function CategoriasTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CAT);
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({}); // errores por campo del modal (patrón validate() canónico)

  const cargar = async () => { try { setRows(await getCategoriasGasto()); } catch (e) { handleApiError(e); } };
  useEffect(() => { cargar(); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // (2026-06-10) Validación canónica del sistema (misma estructura que
  // validate() de ProveedoresForm.js): newErrors por campo + errorToast
  // ("- mensaje") + setErrors + UN solo toast.error con la lista + booleano.
  const validate = () => {
    const newErrors = {};
    const errorToast = [];
    if (!form.codigo) {
      newErrors.codigo = "Código es obligatorio";
      errorToast.push("- Código es obligatorio");
    }
    if (!form.nombre) {
      newErrors.nombre = "Nombre es obligatorio";
      errorToast.push("- Nombre es obligatorio");
    }
    setErrors(newErrors);
    if (errorToast.length > 0) {
      toast.error(
        <div>
          {errorToast.map((item, i) => (
            <span key={i} className="text-start">{item}<br /></span>
          ))}
        </div>
      );
    }
    return Object.keys(newErrors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    try {
      if (editId) await updateCategoriaGasto(editId, form);
      else await createCategoriaGasto(form);
      toast.success("Guardado."); setOpen(false); cargar();
    } catch (e) { handleApiError(e); }
  };

  return (
    <Card>
      <PageHeader
        title="Categorías de gasto"
        subtitle="Catálogo de categorías para clasificar los gastos."
        icon="fa-tags"
        actions={
          <button
            type="button"
            onClick={() => { setForm(EMPTY_CAT); setEditId(null); setErrors({}); setOpen(true); }}
            className="bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2 transition"
          >
            <i className="fas fa-plus" aria-hidden="true" />
            Nueva categoría
          </button>
        }
      />

      <Table>
        <thead>
          <tr>
            <Th>Código</Th>
            <Th>Nombre</Th>
            <Th>Alcance</Th>
            <Th>Crédito fiscal</Th>
            <Th className="text-right">Acc.</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td colSpan={5} className="text-center text-blueGray-400 italic">
                Sin categorías.
              </Td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-blueGray-50 transition-colors">
              <Td className="font-semibold text-blueGray-700">{r.codigo}</Td>
              <Td>{r.nombre}</Td>
              <Td>{r.empresa_id ? "Empresa" : "Global"}</Td>
              <Td><Badge ok={!!r.genera_credito_fiscal} labelOk="Si" labelNo="No" /></Td>
              <Td className="text-right">
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.25rem", whiteSpace: "nowrap" }}>
                  <IconBtn
                    icon="fa-pen-to-square"
                    title="Editar"
                    color="text-blue-600"
                    onClick={() => { setForm({ ...EMPTY_CAT, ...r }); setEditId(r.id); setErrors({}); setOpen(true); }}
                  />
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editId ? "Editar categoría" : "Nueva categoría"}
        icon="fa-tags"
        maxWidth="max-w-[560px]"
        footer={
          <>
            <BtnGhost onClick={() => setOpen(false)}>Cancelar</BtnGhost>
            <BtnPrimary icon="fa-floppy-disk" onClick={save}>Guardar</BtnPrimary>
          </>
        }
      >
        <div className="flex flex-wrap -mx-4">
          <Field label="Código *" className="w-full px-4" error={errors.codigo}>
            <TextInput
              value={form.codigo}
              onChange={(v) => setForm((f) => ({ ...f, codigo: upper(v) }))}
              disabled={!!editId}
            />
          </Field>
          <Field label="Nombre *" className="w-full px-4" error={errors.nombre}>
            <TextInput value={form.nombre} onChange={set("nombre")} />
          </Field>
          <Field label="Estado" className="w-full px-4">
            <div className="py-2">
              <Toggle
                checked={!!form.genera_credito_fiscal}
                onChange={set("genera_credito_fiscal")}
                label="Genera crédito fiscal"
              />
            </div>
          </Field>
        </div>
      </Modal>
    </Card>
  );
}

/* ================================================================== *
 * PÁGINA — AdminLayout + tabs (Proveedores / Categorías de gasto).
 * ================================================================== */
export default function ProveedoresList() {
  const { user } = useContext(AuthContext);
  const [tab, setTab] = useState(0);

  const tabClass = (active) =>
    "px-5 py-2.5 -mb-px text-sm font-display font-semibold border-b-2 transition-colors " +
    (active
      ? "border-sirius-600 text-sirius-600"
      : "border-transparent text-blueGray-500 hover:text-blueGray-700 hover:border-blueGray-300");

  return (
    <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
      <div className="mt-8 px-4 md:px-8">
        <h6 className="text-blueGray-700 text-xl font-display font-bold mb-4">
          Proveedores y categorías
        </h6>

        <div className="flex items-center border-b border-blueGray-200 mb-6">
          <button type="button" className={tabClass(tab === 0)} onClick={() => setTab(0)}>
            <i className="fas fa-truck-field mr-2" aria-hidden="true" />
            Proveedores
          </button>
          <button type="button" className={tabClass(tab === 1)} onClick={() => setTab(1)}>
            <i className="fas fa-tags mr-2" aria-hidden="true" />
            Categorías de gasto
          </button>
        </div>

        {tab === 0 ? <ProveedoresTab /> : <CategoriasTab />}
        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </AdminLayout>
  );
}
